import { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { CloudUploadOutlined, FolderOutlined } from '@ant-design/icons';
import { Button, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { formatDate } from 'helpers/date';
import type { IChargebackList } from 'v2/interfaces/chargeback';
import chargebackService from 'v2/services/chargeback';

interface IProps {
  setModalVisible: (visible: boolean, invoiceId: number) => void;
}

const listStatus = {
  10: 'Aguardando Documentação',
  20: 'Em Análise',
  1: 'Em Processamento',
  3: 'Ganho',
  7: 'Perdido'
};

const List = ({ setModalVisible }: IProps) => {
  const [isLoading] = useObservable(() => chargebackService.isLoading(), []);
  const [data] = useObservable(() => chargebackService.getChargebacks(), []);
  const [pagination] = useObservable(() => chargebackService.getPaginationResponse(), []);

  const onPaginationChange = useCallback((page: number, pageSize: number) => {
    chargebackService.setPagination({ current_page: page, per_page: pageSize });
    document.querySelector('#chargeback_table')?.scrollIntoView();
  }, []);

  const columns: ColumnsType<IChargebackList> = useMemo(
    () => [
      {
        title: 'Fatura',
        key: 'invoice',
        width: 150,
        render: (_: unknown, chargeback) => (
          <>
            <span className='block text-xs'>{chargeback.invoice ? chargeback.invoice.id : '-'}</span>
            <span className='block text-xs font-semibold uppercase'>
              {listStatus[chargeback.id as keyof typeof listStatus]}
            </span>
          </>
        )
      },
      {
        title: 'Solicitado em',
        width: 120,
        dataIndex: 'requested_at',
        key: 'requested_at',
        render: (value: string) => <span>{!!value && formatDate(value)}</span>
      },
      {
        title: 'Cliente',
        key: 'customer',
        width: 200,
        render: (_: unknown, chargeback) => (
          <>
            <span className='block text-xs font-semibold'>{chargeback.customer?.name}</span>
            <span className='block text-xs'>{chargeback.customer?.email}</span>
          </>
        )
      },
      {
        title: 'Produto',
        key: 'product',
        render: (_: unknown, chargeback) => <span>{chargeback.product?.title}</span>
      },
      {
        title: 'Afiliado',
        key: 'affiliate',
        width: 200,
        render: (_: unknown, chargeback) =>
          chargeback.affiliate?.name ? (
            <>
              <span className='block text-xs font-semibold'>{chargeback.affiliate?.name || '-'}</span>
              <span className='block text-xs'>{chargeback.affiliate?.email || '-'}</span>
            </>
          ) : (
            '-'
          )
      },
      {
        title: 'Valor',
        key: 'amount',
        width: 150,
        align: 'right',
        render: (_: unknown, chargeback) => (
          <span>
            <FormattedNumber value={chargeback.invoice ? chargeback.invoice.amount : 0} format='BRL' />
          </span>
        )
      },
      {
        title: 'Limite',
        dataIndex: 'limit_date',
        key: 'limit_date',
        width: 120,
        render: (value: string) => <span>{!!value && formatDate(value)}</span>
      },
      {
        title: 'Ações',
        key: 'actions',
        width: 60,
        align: 'right',
        render: (_: unknown, chargeback) =>
          chargeback.showUploadButton &&
          (!chargeback.hasFiles ? (
            <Tooltip title='Subir arquivo' placement='top'>
              <Button
                type='text'
                shape='circle'
                onClick={() => setModalVisible(true, chargeback.invoice.id)}
                icon={<CloudUploadOutlined />}
              />
            </Tooltip>
          ) : (
            <Tooltip title='Ver arquivos' placement='top'>
              <Button
                type='text'
                shape='circle'
                onClick={() => setModalVisible(true, chargeback.invoice.id)}
                icon={<FolderOutlined />}
              />
            </Tooltip>
          ))
      }
    ],
    [setModalVisible]
  );

  return (
    <div id='chargeback_table' className='w-full overflow-x-auto'>
      <Table<IChargebackList>
        rowKey={chargeback => String(chargeback.invoice?.id)}
        columns={columns}
        dataSource={data ?? []}
        loading={!!isLoading}
        pagination={
          pagination
            ? {
                current: pagination.current_page || 1,
                total: pagination.total_items || 0,
                pageSize: pagination.per_page || 10,
                pageSizeOptions: ['10', '20', '50', '100'],
                showSizeChanger: true,
                onChange: onPaginationChange
              }
            : false
        }
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
      />
    </div>
  );
};

export default List;
